// Acting as a role in a support visit (CR_20260924_Superadmin_from_MobileApp Part C, decisions D–F). Shared by the
// web start dialog / banner switcher and the phone start sheet / visit card: one model, one validation, one mapping
// to the RPC arguments. The server (app.support_act_check) enforces the same rules; this only keeps the buttons honest.
import type { SupportInfo, SupportRole } from './types';

export const SUPPORT_ROLES: readonly SupportRole[] = ['manager', 'warehouse_staff', 'project_user'];

/** A place a role can be given, from `support_role_places(company)`. */
export interface SupportPlace {
  id: string;
  name: string;
  kind: 'warehouse' | 'project' | 'location';
}

/**
 * What the visit acts as. `siteIds` are warehouses for warehouse staff, locations without a project for a project
 * user; `projectIds` are a project user's projects. Both empty unless `scope` is `selected`.
 */
export interface SupportAct {
  role: SupportRole;
  scope: 'all' | 'selected';
  siteIds: string[];
  projectIds: string[];
}

export const MANAGER_ACT: SupportAct = {
  role: 'manager',
  scope: 'all',
  siteIds: [],
  projectIds: [],
};

/** The kinds of place a role is given (decision E): warehouse staff → warehouses; project user → projects + locations. */
export function placeKindsFor(role: SupportRole): SupportPlace['kind'][] {
  if (role === 'warehouse_staff') return ['warehouse'];
  if (role === 'project_user') return ['project', 'location'];
  return [];
}

/** A role change resets the reach to "all" — places picked for one role mean nothing for another. */
export function withRole(role: SupportRole): SupportAct {
  return { role, scope: 'all', siteIds: [], projectIds: [] };
}

/** Tick / untick one place, keeping ids of the right list (projects vs sites). */
export function togglePlace(act: SupportAct, place: SupportPlace): SupportAct {
  const key = place.kind === 'project' ? 'projectIds' : 'siteIds';
  const has = act[key].includes(place.id);
  return {
    ...act,
    [key]: has ? act[key].filter((id) => id !== place.id) : [...act[key], place.id],
  };
}

/** Start / Switch stay disabled while this is false: a manager is always "all"; "selected" needs a place. */
export function actIsValid(act: SupportAct): boolean {
  if (act.role === 'manager') return act.scope === 'all';
  if (act.scope === 'all') return true;
  if (act.role === 'warehouse_staff') return act.siteIds.length > 0 && act.projectIds.length === 0;
  return act.siteIds.length + act.projectIds.length > 0;
}

/** The RPC arguments (start_support_session / set_support_role): ids only when `selected`. */
export function actArgs(act: SupportAct) {
  const selected = act.role !== 'manager' && act.scope === 'selected';
  return {
    p_act_as: act.role,
    p_scope: selected ? 'selected' : 'all',
    p_site_ids: selected && act.siteIds.length ? act.siteIds : null,
    p_project_ids:
      selected && act.role === 'project_user' && act.projectIds.length ? act.projectIds : null,
  };
}

/** The visit's current act, as whoami reports it (a server without Part C reads as manager). */
export function actOf(support: SupportInfo): SupportAct {
  return {
    role: support.act_as ?? 'manager',
    scope: support.act_scope ?? 'all',
    siteIds: support.act_site_ids ?? [],
    projectIds: support.act_project_ids ?? [],
  };
}
